import { Check, TrendingUp, Database, X } from "lucide-react";
import { Reveal, SectionHead, Avatar, ArrowDown } from "../components/bits";

const WITH_STEPS = ["Client identified", "Personalised message sent", "Client replies", "Appointment booked ✓", "Revenue recovered ↑"];
const WITHOUT_STEPS = ["No follow-up", "Client forgets", "No booking", "Client moves on", "Missed opportunity"];

export const TwoPaths = () => (
    <section data-testid="two-paths" className="grain relative overflow-hidden bg-obsidian py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHead
                dark
                align="center"
                eyebrow="Two paths. Two outcomes."
                title="Your past clients will either come back and book — or move on and become someone else’s client."
            />

            <div className="relative mt-14 grid gap-8 lg:grid-cols-[1fr_auto_1fr] lg:gap-6">
                <Reveal>
                    <div data-testid="path-with-summercrest" className="relative h-full rounded-3xl border border-rose/25 bg-matte p-6 sm:p-8">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-rose">With Summercrest</span>
                            <TrendingUp className="h-4 w-4 text-gold" />
                        </div>
                        <div className="relative mt-7">
                            <span
                                className="absolute left-[7px] z-10 h-2 w-2 rounded-full bg-gold shadow-[0_0_10px_rgba(203,163,104,0.8)]"
                                style={{ animation: "travelDown 7s linear infinite" }}
                            />
                            <div className="space-y-0">
                                {WITH_STEPS.map((t, i) => (
                                    <div key={t}>
                                        <div className="flex items-center gap-4">
                                            <span className={`z-0 h-4 w-4 shrink-0 rounded-full border-2 ${i >= 3 ? "border-gold bg-gold/20" : "border-rose/60 bg-rose/10"}`} />
                                            <div className={`flex flex-1 items-center justify-between rounded-xl border px-4 py-3 ${i >= 3 ? "border-gold/30 bg-charcoal" : "border-cream/10 bg-charcoal/50"}`}>
                                                <span className="font-sans text-[12px] uppercase tracking-[0.1em] text-cream/90">{t}</span>
                                                {i === 1 && <span className="font-mono text-[7px] uppercase tracking-widest text-rose">SMS</span>}
                                                {i === 2 && <Avatar initials="S" tone="bg-blush text-ink" />}
                                                {i === 3 && <Check className="h-3.5 w-3.5 text-gold" strokeWidth={2.5} />}
                                                {i === 4 && <TrendingUp className="h-3.5 w-3.5 text-gold" />}
                                            </div>
                                        </div>
                                        {i < WITH_STEPS.length - 1 && (
                                            <div className="py-1 pl-[7px]"><ArrowDown dark /></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-7 border-t border-cream/10 pt-5 font-serif text-lg uppercase leading-snug tracking-tight text-cream">
                            More bookings. More revenue. <span className="italic text-rose">Stronger client relationships.</span>
                        </div>
                        <svg viewBox="0 0 200 36" fill="none" className="mt-4 w-full">
                            <path d="M4 30 C 60 26, 110 18, 196 4" stroke="#CBA368" strokeWidth="1.5" className="line-flow" />
                        </svg>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4 lg:h-full">
                        <span className="hidden h-px w-10 bg-cream/15 lg:block" />
                        <div className="flex flex-col items-center rounded-2xl border border-gold/30 bg-charcoal px-6 py-7 text-center">
                            <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-gold">Today</span>
                            <span className="mt-3 flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-obsidian">
                                <Database className="h-6 w-6 text-gold" strokeWidth={1.25} />
                            </span>
                            <span className="mt-3 font-serif text-lg uppercase leading-tight tracking-tight text-cream">
                                Your client<br />database
                            </span>
                        </div>
                        <div className="hidden flex-1 lg:block" />
                        <span className="hidden h-px w-10 bg-cream/15 lg:block" />
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div data-testid="path-without-automation" className="relative h-full rounded-3xl border border-cream/10 bg-matte/60 p-6 sm:p-8">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-cream/40">Without automation</span>
                            <X className="h-4 w-4 text-cream/30" />
                        </div>
                        <div className="relative mt-7">
                            <span
                                className="absolute left-[7px] z-10 h-2 w-2 rounded-full bg-cream/40"
                                style={{ animation: "travelDown 7s linear infinite" }}
                            />
                            <span
                                className="absolute right-2 top-6 flex items-center gap-2 rounded-full border border-cream/10 bg-charcoal px-3 py-1.5"
                                style={{ animation: "driftAway 5s ease-in-out infinite alternate" }}
                            >
                                <Avatar initials="S" tone="bg-charcoal text-cream/50" />
                                <span className="font-mono text-[7px] uppercase tracking-widest text-cream/40">Client</span>
                            </span>
                            <div className="space-y-0">
                                {WITHOUT_STEPS.map((t, i) => (
                                    <div key={t} style={{ opacity: 1 - i * 0.14 }}>
                                        <div className="flex items-center gap-4">
                                            <span className="z-0 h-4 w-4 shrink-0 rounded-full border-2 border-cream/15 bg-transparent" />
                                            <div className="flex flex-1 items-center justify-between rounded-xl border border-cream/8 bg-charcoal/30 px-4 py-3">
                                                <span className="font-sans text-[12px] uppercase tracking-[0.1em] text-cream/50">{t}</span>
                                                {i === 4 && <X className="h-3.5 w-3.5 text-cream/30" />}
                                            </div>
                                        </div>
                                        {i < WITHOUT_STEPS.length - 1 && (
                                            <div className="py-1 pl-[7px] opacity-40"><ArrowDown /></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-7 border-t border-cream/8 pt-5 font-serif text-lg uppercase leading-snug tracking-tight text-cream/45">
                            Less follow-up. Missed bookings. Lost opportunity.
                        </div>
                        <svg viewBox="0 0 200 36" fill="none" className="mt-4 w-full opacity-50">
                            <path d="M4 8 C 60 12, 110 20, 196 30" stroke="#8C8A94" strokeWidth="1.5" strokeDasharray="4 5" />
                        </svg>
                    </div>
                </Reveal>
            </div>

            <Reveal delay={0.15} className="mt-12 text-center">
                <p className="font-serif text-xl uppercase tracking-tight text-cream sm:text-2xl">
                    Your client database is already an asset.
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                    Show what happens when you act on it — and what happens when you don’t.
                </p>
            </Reveal>
        </div>
    </section>
);
